import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';


const WhyUs = () => {
  const [topEAs, setTopEAs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eaCount, setEaCount] = useState(1);
  const [lastUpdated, setLastUpdated] = useState('');

  // Fixed values for the calculator based on screenshot
  const avgMqlPrice = 275;
  const avgVaultPrice = 39;
  const savingsPerEa = avgMqlPrice - avgVaultPrice; // 236
  const totalSavings = eaCount * savingsPerEa;

  useEffect(() => {
    // Generate a timestamp for the "last updated" text
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    setLastUpdated(`${hours}:${minutes} ${ampm}`);

    fetch(`${API_URL}/api/products`)
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) {
          console.error("API did not return an array of products:", data);
          setTopEAs([]);
          setLoading(false);
          return;
        }
        
        // Calculate savings for each EA
        const withSavings = data.filter(ea => ea.oldPrice && ea.newPrice).map(ea => {
          const mqlPrice = parseFloat(ea.oldPrice);
          const vaultPrice = parseFloat(ea.newPrice);
          const savingAmt = mqlPrice - vaultPrice;
          const savingPct = Math.round((savingAmt / mqlPrice) * 100);
          return {
            ...ea,
            mqlPrice,
            vaultPrice,
            savingAmt,
            savingPct
          };
        });

        // Sort by biggest saving amount descending
        withSavings.sort((a, b) => b.savingAmt - a.savingAmt);
        
        // Take top 5
        setTopEAs(withSavings.slice(0, 5));
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Calculate totals for the table
  const totalMql = topEAs.reduce((sum, ea) => sum + ea.mqlPrice, 0);
  const totalVault = topEAs.reduce((sum, ea) => sum + ea.vaultPrice, 0);
  const totalSavingsAmt = totalMql - totalVault;
  const avgSavingsPct = totalMql > 0 ? Math.round((totalSavingsAmt / totalMql) * 100) : 0;

  return (
    <div className="pt-24 min-h-screen bg-transparent">
      {/* Hero Section */}
      <section className="relative text-center px-margin-mobile md:px-margin-desktop mb-24 max-w-4xl mx-auto z-10 animate-fade-in">
        <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary mb-8">
          <span className="font-label-sm text-[11px] uppercase tracking-[0.2em] font-bold">THE VALUE PROPOSITION</span>
        </div>
        
        <h1 className="font-display-lg text-display-lg md:text-[64px] md:leading-[1.1] mb-6 font-bold tracking-tight">
          Why Pay Full Price on <span className="text-primary">MQL5</span>?
        </h1>
        
        <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl mx-auto text-lg leading-relaxed">
          We offer the exact same top-rated EAs at a fraction of the cost. Same features.
          Same version. Same performance. Zero compromise.
        </p>
      </section>

      {/* Direct Price Comparison Table */}
      <section className="max-w-[1200px] mx-auto px-margin-mobile md:px-margin-desktop mb-24 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-6">
          <div>
            <h2 className="font-display-md text-headline-md font-bold mb-1 tracking-tight">Direct Price Comparison</h2>
            <p className="text-on-surface-variant text-sm">Live from our catalog — the top 5 EAs with the biggest savings right now.</p>
          </div>
          <div className="text-[10px] text-on-surface-variant/70 uppercase tracking-widest mt-4 md:mt-0 font-bold">
            UPDATED {lastUpdated} · AUTO-REFRESHES EVERY 5 MIN
          </div>
        </div>

        <div className="bg-surface-container-lowest/80 backdrop-blur-md rounded-2xl border border-white/5 overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="py-6 px-8 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest w-2/5">EA Name</th>
                  <th className="py-6 px-4 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest text-center">Platform</th>
                  <th className="py-6 px-4 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest text-center">MQL5 Store Price</th>
                  <th className="py-6 px-4 text-[10px] font-bold text-primary uppercase tracking-widest text-center">Vault Price</th>
                  <th className="py-6 px-8 text-[10px] font-bold text-[#25D366] uppercase tracking-widest text-right">Your Direct Savings</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-on-surface-variant">Loading comparison data...</td>
                  </tr>
                ) : (
                  topEAs.map((ea, index) => (
                    <tr key={ea.id || index} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-5 px-8 font-bold text-[14px] text-silver-text">{ea.name}</td>
                      <td className="py-5 px-4 text-center">
                        <span className="bg-white/5 border border-white/10 rounded px-2 py-1 text-[10px] font-bold text-on-surface-variant uppercase">
                          {ea.platform || 'MT5 EA'}
                        </span>
                      </td>
                      <td className="py-5 px-4 text-center text-on-surface-variant line-through text-[14px]">
                        ${ea.mqlPrice}
                      </td>
                      <td className="py-5 px-4 text-center text-primary font-bold text-[16px]">
                        ${ea.vaultPrice}
                      </td>
                      <td className="py-5 px-8 text-right font-bold text-[#25D366] text-[14px]">
                        Save ${ea.savingAmt} ({ea.savingPct}% OFF)
                      </td>
                    </tr>
                  ))
                )}
                
                {!loading && topEAs.length > 0 && (
                  <tr className="bg-white/[0.02]">
                    <td className="py-6 px-8 font-bold text-[13px] text-on-surface-variant uppercase tracking-widest">
                      Total Cost (Top 5 Deals)
                    </td>
                    <td className="py-6 px-4"></td>
                    <td className="py-6 px-4 text-center text-on-surface-variant line-through text-[14px]">
                      ${totalMql}
                    </td>
                    <td className="py-6 px-4 text-center text-primary font-bold text-[16px]">
                      ${totalVault}
                    </td>
                    <td className="py-6 px-8 text-right font-bold text-[#25D366] text-[15px]">
                      Save ${totalSavingsAmt} ({avgSavingsPct}% avg OFF)
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Savings Calculator Section */}
      <section className="max-w-[1200px] mx-auto px-margin-mobile md:px-margin-desktop mb-32 relative z-10">
        <div className="bg-surface-container-lowest/80 backdrop-blur-md rounded-3xl border border-white/5 overflow-hidden shadow-2xl p-8 md:p-12 flex flex-col lg:flex-row items-center gap-12">
          
          {/* Left: Slider */}
          <div className="w-full lg:w-1/2">
            <h2 className="font-display-md text-headline-md font-bold mb-4 tracking-tight">Savings Calculator</h2>
            <p className="text-on-surface-variant text-sm mb-10 leading-relaxed">
              Plan on diversifying your automated trading desk? Use the slider below to calculate how much trading capital you keep in your brokerage account by shopping with Vault.
            </p>
            
            <div className="mb-8">
              <div className="flex justify-between items-center mb-6">
                <span className="font-bold text-[14px]">Number of EAs planned:</span>
                <span className="text-primary font-bold">{eaCount} EA{eaCount !== 1 ? 's' : ''}</span>
              </div>
              
              <input 
                type="range" 
                min="1" 
                max="10" 
                value={eaCount} 
                onChange={(e) => setEaCount(parseInt(e.target.value))}
                className="w-full h-2 bg-surface-container-high rounded-lg appearance-none cursor-pointer accent-primary"
                style={{
                  background: `linear-gradient(to right, #f0b90b 0%, #f0b90b ${(eaCount - 1) * 11.11}%, #2A2E33 ${(eaCount - 1) * 11.11}%, #2A2E33 100%)`
                }}
              />
              
              <div className="flex justify-between items-center mt-3 text-[10px] text-on-surface-variant font-bold">
                <span>1 EA</span>
                <span>5 EAs</span>
                <span>10 EAs</span>
              </div>
            </div>
          </div>
          
          {/* Right: Summary Card */}
          <div className="w-full lg:w-1/2">
            <div className="bg-[#121518] rounded-2xl p-8 text-center border border-white/5 shadow-inner">
              <div className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mb-4">Total Savings Summary</div>
              <div className="text-6xl md:text-7xl font-bold text-[#25D366] mb-6 tracking-tight">${totalSavings}</div>
              <div className="text-[15px] font-medium text-silver-text mb-4">
                Buying <span className="text-primary font-bold">{eaCount} EA{eaCount !== 1 ? 's' : ''}</span> from us saves you <span className="text-[#25D366] font-bold">${totalSavings}</span> vs buying on MQL5.
              </div>
              <div className="text-[11px] text-on-surface-variant/60">
                (Calculated using an average MQL5 price of ${avgMqlPrice} vs our price of ${avgVaultPrice})
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Same EA Header */}
      <section className="text-center px-margin-mobile md:px-margin-desktop mb-16 relative z-10">
        <h2 className="font-display-md text-headline-sm md:text-headline-md font-bold mb-4">
          Same EA. Same Version. Just Cheaper.
        </h2>
        <p className="text-on-surface-variant max-w-2xl mx-auto text-[15px]">
          Our files are the official unlocked packages exported directly from MT4/MT5 strategy platforms. We guarantee total fidelity.
        </p>
      </section>

      {/* Value Pillars Cards */}
      <section className="max-w-[1200px] mx-auto px-margin-mobile md:px-margin-desktop grid grid-cols-1 md:grid-cols-3 gap-6 mb-24 relative z-10">
        {/* Card 1 */}
        <div className="bg-surface-container-lowest/80 backdrop-blur-md border border-white/5 rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300">
          <div className="text-[#ff6b6b] mb-6">
            <span className="material-symbols-outlined text-[32px]">bolt</span>
          </div>
          <h3 className="font-bold text-[18px] text-silver-text mb-3">Instant Digital Delivery</h3>
          <p className="text-on-surface-variant text-[13px] leading-relaxed">
            Immediately after verifying payment, download files and optimal preset setfiles directly from your accounts console or your inbox.
          </p>
        </div>

        {/* Card 2 */}
        <div className="bg-surface-container-lowest/80 backdrop-blur-md border border-white/5 rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300">
          <div className="text-[#4dabf7] mb-6">
            <span className="material-symbols-outlined text-[32px]">sync</span>
          </div>
          <h3 className="font-bold text-[18px] text-silver-text mb-3">Lifetime Free Access</h3>
          <p className="text-on-surface-variant text-[13px] leading-relaxed">
            Pay once. Enjoy lifetime updates and setfile downloads for free. We continuously sync with the latest author updates.
          </p>
        </div>

        {/* Card 3 */}
        <div className="bg-surface-container-lowest/80 backdrop-blur-md border border-white/5 rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300">
          <div className="text-[#69db7c] mb-6">
            <span className="material-symbols-outlined text-[32px]">bar_chart</span>
          </div>
          <h3 className="font-bold text-[18px] text-silver-text mb-3">All Backtests Verified</h3>
          <p className="text-on-surface-variant text-[13px] leading-relaxed">
            Every EA is fully loaded with strategy tester equity charts, drawdown records, and optimized parameters to protect your capital.
          </p>
        </div>
      </section>

      {/* Call To Action */}
      <section className="text-center pb-32 relative z-10">
        <Link to="/marketplace" className="inline-flex items-center space-x-2 bg-primary text-on-primary font-bold px-8 py-4 rounded-xl hover:scale-105 active:scale-95 transition-all btn-shimmer shadow-xl shadow-primary/20">
          <span>Browse Our EA Collection</span>
          <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
        </Link>
      </section>

    </div>
  );
};

export default WhyUs;
